import { GAME_FX } from '@utils/GameEffects';

export default class GameButton extends Phaser.GameObjects.Sprite {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} x
   * @param {number} y
   * @param {string} texture - Texture key, or any value when using config.text
   * @param {Object} config
   * @param {string} [config.text] - Label text. Auto-generates a yellow pill button
   *   with dark hover and green clicked states. When provided, the texture param is ignored.
   * @param {string} [config.fill='#ffb700'] - Button fill color (text mode)
   * @param {string} [config.fillHover='#333333'] - Hover fill color (text mode)
   * @param {string} [config.fillClicked='#BFBFBF'] - Clicked fill color (text mode)
   * @param {string} [config.stroke='#333333'] - Border color (text mode)
   * @param {string} [config.textColor='#333333'] - Label color (text mode)
   * @param {string} [config.textColorHover='#FFFFFF'] - Hover label color (text mode)
   */
  constructor(scene, x, y, texture, config = {}) {
    // Auto-generate pill button textures when text is provided
    if (config.text) {
      const keys = GameButton._generateTextButton(scene, config.text, config);
      texture = keys.normal;
      config._autoHover = keys.hover;
      config._autoClicked = keys.clicked;
    }

    super(scene, x, y, texture);
    scene.add.existing(this);

    this.setInteractive({ cursor: 'pointer' });

    this.normalTexture = texture;
    this.hoverTexture = config._autoHover || null;
    this.clickedTexture = config._autoClicked || null;

    this.enabled = true;
    this.isDown = false;

    this.shouldPlayEffects = true;
    this.effects = {
      hoverScale: config.hoverScale ?? 1.02,
      pressScale: config.pressScale ?? 0.98,
      tweenDuration: config.tweenDuration ?? 120,
    };

    this.onClickCallback;

    this.__setupEvents();
  }

  // ==================== TEXT BUTTON GENERATION ====================

  static _generateTextButton(scene, text, config = {}) {
    const sanitized = text.toLowerCase().replace(/[^a-z0-9]/g, '_');
    const normalKey = `_textbtn_${sanitized}`;
    const hoverKey = `${normalKey}_hover`;
    const clickedKey = `${normalKey}_clicked`;

    if (scene.textures.exists(normalKey)) {
      return { normal: normalKey, hover: hoverKey, clicked: clickedKey };
    }

    // Measure text to auto-size button width
    const tmpCanvas = document.createElement('canvas');
    const tmpCtx = tmpCanvas.getContext('2d');
    tmpCtx.font = 'bold 22px Arial, sans-serif';
    const textW = tmpCtx.measureText(text).width;

    const pad = 40;
    const w = Math.max(Math.ceil(textW + pad * 2), 100);
    const h = 48;
    const r = h / 2;

    const fillNormal = config.fill || '#ffb700';
    const fillHover = config.fillHover || '#333333';
    const fillClicked = config.fillClicked || '#BFBFBF';
    const strokeNormal = config.stroke || '#333333';
    const strokeClicked = config.strokeClicked || '#BFBFBF';
    const txtNormal = config.textColor || '#333333';
    const txtHover = config.textColorHover || '#FFFFFF';
    const txtClicked = config.textColorClicked || '#2F2F2F';

    const draw = (key, fill, stroke, txtColor) => {
      const canvas = scene.textures.createCanvas(key, w, h);
      const ctx = canvas.context;

      ctx.beginPath();
      ctx.arc(r, r, r - 1, Math.PI * 0.5, Math.PI * 1.5);
      ctx.arc(w - r, r, r - 1, Math.PI * 1.5, Math.PI * 0.5);
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = txtColor;
      ctx.font = 'bold 22px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, w / 2, h / 2 + 1);

      canvas.refresh();
    };

    draw(normalKey, fillNormal, strokeNormal, txtNormal);
    draw(hoverKey, fillHover, strokeNormal, txtHover);
    draw(clickedKey, fillClicked, strokeClicked, txtClicked);

    return { normal: normalKey, hover: hoverKey, clicked: clickedKey };
  }

  __setupEvents() {
    this.on('pointerover', () => {
      if (!this.enabled || this.isDown) return;

      if (this.hoverTexture) {
        GAME_FX.switchTexture(this, this.hoverTexture, 150);
      }
      if (!this.shouldPlayEffects) return;
      GAME_FX.resize(this, this.effects.hoverScale, this.effects.tweenDuration);
    });

    this.on('pointerout', () => {
      if (!this.enabled) return;

      this.isDown = false;

      GAME_FX.switchTexture(this, this.normalTexture, 200);
      if (!this.shouldPlayEffects) return;
      GAME_FX.resize(this, 1, this.effects.tweenDuration, { ease: 'Sine.easeIn' });
    });

    this.on('pointerdown', () => {
      if (!this.enabled) return;

      this.isDown = true;

      if (!this.shouldPlayEffects) return;
      GAME_FX.resize(this, this.effects.pressScale, this.effects.tweenDuration);
    });

    this.on('pointerup', () => {
      if (!this.enabled || !this.isDown) return;

      this.isDown = false;

      if (this.clickedTexture) {
        this.setActive(false);
        GAME_FX.switchTexture(this, this.clickedTexture, 200);
      }

      this.onClickCallback?.();
    });
  }

  setNormalTexture(texture) {
    this.normalTexture = texture;
    return this;
  }

  setHoverTexture(texture) {
    this.hoverTexture = texture;
    return this;
  }

  setClickedTexture(texture) {
    this.clickedTexture = texture;
    return this;
  }

  onClick(callback = null) {
    this.onClickCallback = callback;
    return this;
  }

  disableEffects() {
    this.shouldPlayEffects = false;
    return this;
  }

  setActive(value) {
    if (value) {
      this.enabled = true;
      this.setTexture(this.normalTexture);
      this.setInteractive({ cursor: 'pointer' });
    } else {
      this.enabled = false;
      this.removeInteractive();
    }
    return super.setActive(value);
  }
}
